import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ModelSelector } from "@/components/model-selector";
import { ArrowLeft, Sparkles, Loader2, Save } from "lucide-react";
import type { AIModelId } from "@/lib/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { reportTemplates } from "@/lib/report-templates";

export default function QueryPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [prompt, setPrompt] = useState("");
  const [title, setTitle] = useState("");
  const [selectedModel, setSelectedModel] = useState<AIModelId>("openai");
  const [response, setResponse] = useState("");
  const [showSaveForm, setShowSaveForm] = useState(false);

  // Handle template parameter from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const templateId = urlParams.get('template');
    
    if (templateId && reportTemplates[templateId as keyof typeof reportTemplates]) {
      const template = reportTemplates[templateId as keyof typeof reportTemplates];
      setPrompt(template.prompt);
      setTitle(template.name);
    }
  }, []);

  const generateMutation = useMutation({
    mutationFn: async (data: { prompt: string; model: AIModelId }) => {
      const result = await apiRequest("POST", "/api/ai/query", data);
      return result as { response: string };
    },
    onSuccess: (data) => {
      setResponse(data.response);
      setShowSaveForm(true);
      if (!title) {
        const firstLine = prompt.split('\n')[0];
        setTitle(firstLine.slice(0, 100) || "Research Report");
      }
    },
    onError: (error: any) => {
      toast({
        title: "Query failed",
        description: error.message || "Could not generate response. Please try again.",
        variant: "destructive",
      });
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: { title: string; prompt: string; modelUsed: string; content: string }) => {
      return await apiRequest("POST", "/api/reports", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/reports"] });
      toast({
        title: "Report saved",
        description: "Your research report has been saved successfully",
      });
      setLocation("/");
    },
    onError: (error: any) => {
      toast({
        title: "Save failed",
        description: error.message || "Could not save the report. Please try again.",
        variant: "destructive",
      });
    },
  });

  function handleGenerate() {
    if (!prompt.trim()) {
      toast({
        title: "Empty prompt",
        description: "Please enter a research question or topic",
        variant: "destructive",
      });
      return;
    }
    generateMutation.mutate({ prompt, model: selectedModel });
  }

  function handleSave() {
    if (!title.trim()) {
      toast({
        title: "Title required",
        description: "Please provide a title for your report",
        variant: "destructive",
      });
      return;
    }
    saveMutation.mutate({
      title,
      prompt,
      modelUsed: selectedModel,
      content: response,
    });
  }

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => setLocation("/")}
          data-testid="button-back"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-4xl font-bold" data-testid="text-page-title">Generate Research Report</h1>
          <p className="text-muted-foreground mt-2" data-testid="text-page-description">
            Choose an AI model and describe your research question
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Research Query</CardTitle>
          </CardHeader>
          <CardContent>
            <ModelSelector
              selected={selectedModel}
              onSelect={setSelectedModel}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Research Prompt</CardTitle>
            <CardDescription>
              Describe what you want to research. Be specific about startups, markets, or business ideas.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="Example: Analyze the competitive landscape for AI-powered project management tools targeting remote teams. Include market size, key players, and growth opportunities."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="min-h-32 font-mono text-sm leading-relaxed resize-none"
              data-testid="input-prompt"
            />
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {prompt.length} characters
              </span>
              <Button
                onClick={handleGenerate}
                disabled={generateMutation.isPending}
                className="h-12"
                data-testid="button-generate"
              >
                {generateMutation.isPending ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {response && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>AI Response</CardTitle>
                <Badge variant="outline" className="bg-chart-1/10 text-chart-1">
                  {selectedModel}
                </Badge>
              </div>
              <CardDescription>Generated research analysis</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg bg-muted/50 p-6">
                <pre className="font-mono text-sm leading-relaxed whitespace-pre-wrap" data-testid="text-response">
                  {response}
                </pre>
              </div>

              {showSaveForm && (
                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="report-title">Report Title</Label>
                    <Input
                      id="report-title"
                      placeholder="Enter a title for this report"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="h-12"
                      data-testid="input-title"
                    />
                  </div>
                  <div className="flex justify-end gap-4">
                    <Button
                      variant="outline"
                      onClick={() => setLocation("/")}
                      data-testid="button-discard"
                    >
                      Discard
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saveMutation.isPending}
                      data-testid="button-save"
                    >
                      {saveMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Report
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}